import {
    RequestBucketUrlBody,
    RequestCreateStudentStatus,
    RequestRemoveStudentStatusDocument,
    RequestRemoveUsers,
    RequestResendEmail,
    RequestStudentStatusDocumentGenerateUpload,
    RequestStudentStatusDocumentUpload,
    RequestUserPhoto,
    RequestVerifyEmail,
} from 'entities/session'
import type {
    CommentsData,
    HistoryData,
    StudentStatusListData,
    User,
} from '../model/types'
import type { DataWithMeta } from 'shared/lib'
import { baseApi } from 'shared/api/baseApi'
import {
    INDIVIDUAL_USER,
    ORGANIZATION_UNIT_TAG,
    STUDENT_STATUS_TAG,
    USER,
    USERS_TAG,
} from 'shared/api/tags'

export const userApi = baseApi.injectEndpoints({
    overrideExisting: true,
    endpoints: (build) => ({
        getUser: build.query<User, string>({
            query: () => ({
                url: `users/self`,
                method: 'GET',
            }),
            providesTags: [USER, INDIVIDUAL_USER],

            serializeQueryArgs: ({ endpointName }) => {
                return endpointName
            },

            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),

        getIndividualUser: build.query<User, string>({
            query: (id) => ({
                url: `users/${id}`,
                method: 'GET',
            }),

            providesTags: [INDIVIDUAL_USER],
        }),

        getUsers: build.query<DataWithMeta<User>, Params>({
            query: (params) => ({
                url: `users`,
                method: 'GET',
                params,
            }),

            providesTags: [USERS_TAG],
        }),

        removeUsers: build.mutation<ApiResponse<User>, RequestRemoveUsers>({
            query: (body) => ({
                url: `users?ids=${body.ids}`,
                method: 'DELETE',
            }),
            invalidatesTags: [USERS_TAG],
        }),

        createStudentStatus: build.mutation<
            ApiResponse<User>,
            RequestCreateStudentStatus
        >({
            query: (body) => ({
                url: `users/${body.id}/student-statuses`,
                method: 'POST',
                body: { seasonId: body.seasonId },
            }),
            invalidatesTags: [USERS_TAG, INDIVIDUAL_USER, STUDENT_STATUS_TAG],
        }),

        generateStudentStatusUploadUrl: build.mutation<
            ApiResponse<unknown>,
            RequestStudentStatusDocumentGenerateUpload
        >({
            query: (body) => ({
                url: `users/${body.userId}/student-statuses/${body.studentStatusId}/documents/generate-upload-url`,
                method: 'POST',
            }),
        }),

        uploadStudentStatusDocument: build.mutation<
            ApiResponse<unknown>,
            RequestStudentStatusDocumentUpload
        >({
            query: (body) => ({
                url: `users/${body.userId}/student-statuses/${body.studentStatusId}/documents`,
                method: 'POST',
                body: { key: body.key, type: body.type, name: body.name },
            }),
            invalidatesTags: [STUDENT_STATUS_TAG],
        }),

        getStudentStatus: build.query<StudentStatusListData, string>({
            query: (id) => ({
                url: `users/${id}/student-statuses`,
                method: 'GET',
            }),
            providesTags: [STUDENT_STATUS_TAG],
        }),
        getBucketUrl: build.mutation<ApiResponse<User>, RequestBucketUrlBody>({
            query: (body) => ({
                url: `users/${body.id}/photo/generate-upload-url`,
                method: 'POST',
                body,
            }),
            invalidatesTags: [
                USER,
                USERS_TAG,
                ORGANIZATION_UNIT_TAG,
                INDIVIDUAL_USER,
            ],
        }),

        uploadUserPhoto: build.mutation<ApiResponse<User>, RequestUserPhoto>({
            query: (body) => ({
                url: `users/${body.id}/photo`,
                method: 'POST',
                body: { key: body.key },
            }),

            invalidatesTags: [
                USER,
                USERS_TAG,
                ORGANIZATION_UNIT_TAG,
                INDIVIDUAL_USER,
            ],
        }),
        verifyEmail: build.mutation<ApiResponse<User>, RequestVerifyEmail>({
            query: (body) => ({
                url: `auth/verify-email`,
                method: 'POST',
                body,
            }),
        }),
        resendEmailVerification: build.mutation<
            ApiResponse<User>,
            RequestResendEmail
        >({
            query: (body) => ({
                url: `auth/resend-email-verification`,
                method: 'POST',
                body: { email: body?.email },
            }),
        }),

        removeStudentStatusDocument: build.mutation<
            ApiResponse<User>,
            RequestRemoveStudentStatusDocument
        >({
            query: (body) => ({
                url: `users/${body.userId}/documents/${body.documentId}`,
                method: 'DELETE',
            }),
            invalidatesTags: [STUDENT_STATUS_TAG],
        }),

        getHistory: build.query<HistoryData[], string>({
            query: (id) => ({
                url: `users/${id}/history`,
                method: 'GET',
            }),
        }),

        getComments: build.query<CommentsData[], string>({
            query: (id) => ({
                url: `users/${id}/comments`,
                method: 'GET',
            }),
        }),
    }),
})
export const {
    useGetBucketUrlMutation,
    useGetUserQuery,
    useUploadUserPhotoMutation,
    useGetStudentStatusQuery,
    useCreateStudentStatusMutation,
    useRemoveStudentStatusDocumentMutation,
    useVerifyEmailMutation,
    useResendEmailVerificationMutation,
    useGenerateStudentStatusUploadUrlMutation,
    useGetHistoryQuery,
    useGetCommentsQuery,
    useGetUsersQuery,
    useLazyGetUsersQuery,
    useRemoveUsersMutation,
    useGetIndividualUserQuery,
} = userApi
